import { formatDoj, tenureOf } from "@/lib/tenure";
import React from "react";
import { useNavigate } from "react-router-dom";

const initials = (name = "") =>
    name.split(" ").map((x) => x[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

const accentFromId = (id = "") => {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    const palette = [
        "bg-teal-100 text-[#1F7F79]",
        "bg-indigo-100 text-indigo-700",
        "bg-amber-100 text-amber-700",
        "bg-rose-100 text-rose-700",
        "bg-violet-100 text-violet-700",
        "bg-sky-100 text-sky-700",
        "bg-emerald-100 text-emerald-700",
        "bg-fuchsia-100 text-fuchsia-700",
    ];
    return palette[h % palette.length];
};

const humanRole = (r, hasChildren = false) => {
    if (r === "employee" && hasChildren) return "Lead";
    return ({
        super_admin: "Super Admin",
        manager: "Manager",
        coordinator: "Coordinator",
        employee: "Member",
    }[r] || r);
};

const OrgTreeNode = ({ node, matches, currentUserId }) => {
    const navigate = useNavigate();
    const hasChildren = (node.children?.length || 0) > 0;
    const isDimmed = matches && !matches.has(node.user_id);
    const titleText = node.designation || node.position || humanRole(node.role, hasChildren);
    const isAdmin = node.role === "super_admin";
    const isManager = node.role === "manager";
    const isMe = node.user_id === currentUserId;
    return (
        <li>
            <button
                type="button"
                onClick={() => navigate(`/employees/${node.user_id}`)}
                data-testid={`org-card-${node.user_id}`}
                className={`group inline-flex flex-col items-center gap-1 min-w-[160px] max-w-[200px] px-3 py-3 rounded-lg border bg-white transition-all ${
                    isDimmed
                        ? "opacity-30 border-slate-100"
                        : isMe
                        ? "border-[#2EB4AC] shadow-md org-node-me"
                        : isAdmin
                        ? "border-slate-900/40 hover:border-slate-900 hover:shadow-md"
                        : isManager
                        ? "border-[#2EB4AC]/40 hover:border-[#2EB4AC] hover:shadow-md"
                        : "border-slate-200 hover:border-slate-400 hover:shadow-sm"
                }`}
            >
                <div
                    className={`h-10 w-10 rounded-full flex items-center justify-center font-heading text-xs font-semibold ${accentFromId(node.user_id)} ${
                        isMe ? "ring-2 ring-offset-2 ring-[#2EB4AC]" : isAdmin ? "ring-2 ring-offset-2 ring-slate-900/80" : isManager ? "ring-2 ring-offset-2 ring-[#2EB4AC]/60" : ""
                    }`}
                >
                    {initials(node.name)}
                </div>
                <div className="text-sm font-medium text-slate-900 truncate max-w-full flex items-center gap-1.5" title={node.name}>
                    {node.name}
                    {isMe && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-bold tracking-wider bg-[#2EB4AC] text-white shrink-0">
                            YOU
                        </span>
                    )}
                </div>
                <div className="text-[10px] text-slate-500 truncate max-w-full">{titleText}</div>
                {node.sub_department && (
                    <div className="text-[10px] text-slate-400 truncate max-w-full">· {node.sub_department}</div>
                )}
                {node.date_of_joining && (
                    <div className="text-[10px] text-slate-500 truncate max-w-full" title={`Joined ${formatDoj(node.date_of_joining)}`} data-testid={`orgtree-doj-${node.user_id}`}>
                        Joined {formatDoj(node.date_of_joining)} · <span className="font-semibold text-amber-700">{tenureOf(node.date_of_joining)}</span>
                    </div>
                )}
                {hasChildren && (
                    <div className="text-[9px] text-slate-400 mt-0.5">
                        {node.children.length} report{node.children.length !== 1 ? "s" : ""}
                    </div>
                )}
            </button>
            {hasChildren && (
                <ul>
                    {node.children.map((c) =>
                        // Use React.createElement here instead of <OrgTreeNode /> JSX so the
                        // visual-edits babel plugin doesn't blow up walking the recursive
                        // self-reference at build time.
                        React.createElement(OrgTreeNode, { key: c.user_id, node: c, matches, currentUserId })
                    )}
                </ul>
            )}
        </li>
    );
};

export default OrgTreeNode;
